"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useMemo } from "react";
import { ArrowRight, Calendar, CircleCheck as CheckCircle2, Stethoscope, UserCheck } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const fallbackArticles = [{
  id: "fb-1",
  title: "Signs of allergies in dogs",
  excerpt: "Learn how to differentiate environmental, seasonal, and food sensitivities in canines before secondary skin infections develop.",
  link: "/PetHealthPage",
  date: "May 12, 2025",
  category: "Canine Immunology",
  reviewer: "Dr. Aris Thorne, DVM",
  takeaway: "Persistent paw-licking and recurrent ear redness often indicate environmental atopy rather than simple dietary triggers.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/dog_allergy_clinical_check.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/dog_allergy_clinical_check.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/dog_allergy_clinical_check.png-wvc-sizes",
    alt: "Veterinarian carefully examining a dog's coat and skin for allergen symptoms"
  }
}, {
  id: "fb-2",
  title: "Feline kidney disease early warning signs",
  excerpt: "Recognize subtle behavioral, hydration, and weight changes that signal early stage renal changes in senior cats.",
  link: "/PetHealthPage",
  date: "April 28, 2025",
  category: "Feline Nephrology",
  reviewer: "Dr. Elena Vance, DACVIM",
  takeaway: "Subtle increases in water consumption and gradual muscle loss along the spine precede detectable bloodwork changes in Stage II CKD.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/cat_kidney_health_vet.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/cat_kidney_health_vet.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/cat_kidney_health_vet.webp-wvc-sizes",
    alt: "Veterinary clinician evaluating a calm senior cat during a health examination"
  }
}, {
  id: "fb-3",
  title: "Preventative dental care for pets",
  excerpt: "A clinically structured approach to managing periodontal bacteria, plaque calcification, and home maintenance routines.",
  link: "/PetHealthPage",
  date: "April 14, 2025",
  category: "Oral Health & Surgery",
  reviewer: "Dr. Marcus Reed, DVM, FAVD",
  takeaway: "Subgingival bacteria can compromise systemic renal and cardiac function if tartar is left untreated beyond grade 1 gingivitis.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/pet_dental_cleaning_examination.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/pet_dental_cleaning_examination.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/pet_dental_cleaning_examination.png-wvc-sizes",
    alt: "Close-up clinical inspection of canine dental health and gums"
  }
}];
function ArticlesConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map((_, i) => <div key={i} data-index={i} className="flex flex-col overflow-hidden rounded-xl border border-border bg-card p-6 shadow-xs animate-pulse">
            <div className="h-52 w-full rounded-lg bg-muted" />
            <div className="mt-6 h-4 w-1/3 rounded-sm bg-muted" />
            <div className="mt-3 h-6 w-3/4 rounded-sm bg-muted" />
            <div className="mt-3 h-16 w-full rounded-sm bg-muted" />
            <div className="mt-6 h-20 w-full rounded-lg bg-muted/60" />
            <div className="mt-6 h-10 w-full rounded-md bg-muted" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-xl border border-border bg-card p-8 text-center">
        <p className="text-sm text-muted-foreground">
          Unable to synchronize with medical archives.
        </p>
        <Button variant="outline" size="sm" onClick={refetch} className="mt-4">
          Retry archive query
        </Button>
      </div>;
  }
  const items = posts && posts.length > 0 ? posts.slice(0, 3) : null;
  if (items) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {items.map((post, i) => {
        const categoryName = post.categories && post.categories.length > 0 ? post.categories[0].name : "Pet Health";
        const formattedDate = post.date ? new Date(post.date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric"
        }) : "Recent review";
        const reviewerName = post.customFields && post.customFields.reviewer || post.author && post.author.name || "Veterinary Editorial Board";
        const clinicalTakeaway = post.customFields && post.customFields.takeaway || post.excerpt?.replace(/<[^>]+>/g, "").slice(0, 110) + "...";
        return <article key={post.id} data-index={i} className="flex h-full flex-col">
              <Card className="flex h-full flex-col overflow-hidden border-border bg-card transition-[border-color,box-shadow] duration-200 hover:border-border/80 hover:shadow-md">
                {post.featuredImage && <div className="relative aspect-16/10 w-full overflow-hidden bg-muted">
                    <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 hover:scale-103" />
                  </div>}
                <CardHeader className="space-y-3 p-6 pb-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Badge variant="secondary" className="font-semibold text-foreground">
                      {categoryName}
                    </Badge>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <SafeHydrate><Calendar className="h-3.5 w-3.5" /></SafeHydrate>
                      <span>{formattedDate}</span>
                    </div>
                  </div>
                  <h3 className="font-serif text-xl font-semibold leading-snug text-foreground">
                    {post.title}
                  </h3>
                </CardHeader>
                <CardContent className="flex-1 space-y-4 p-6 pt-2">
                  <div className="text-sm leading-relaxed text-muted-foreground line-clamp-3" dangerouslySetInnerHTML={{
                __html: post.excerpt || post.content?.slice(0, 140) || ""
              }} />

                  {clinicalTakeaway && <div className="rounded-lg border border-border border-l-4 border-l-foreground/80 bg-background/80 p-4">
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        <span>Clinical summary</span>
                      </div>
                      <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                        {clinicalTakeaway}
                      </p>
                    </div>}

                  <div className="flex items-center gap-2 border-t border-border pt-4">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary text-secondary-foreground">
                      <UserCheck className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-medium text-foreground">
                        Reviewed by {reviewerName}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        Evidence verified
                      </p>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="p-6 pt-0">
                  <Button asChild variant="default" className="w-full justify-between">
                    <Link to={post.link || "/PetHealthPage"}>
                      <span>Read clinical guide</span>
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </article>;
      })}
      </div>;
  }
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
      {fallbackArticles.map((article, i) => <article key={article.id} data-index={i} className="flex h-full flex-col">
          <Card className="flex h-full flex-col overflow-hidden border-border bg-card transition-[border-color,box-shadow] duration-200 hover:border-border/80 hover:shadow-md">
            <div className="relative aspect-16/10 w-full overflow-hidden bg-muted">
              <img src={article.image.src} data-wvc-srcset={article.image.srcset} data-wvc-sizes={article.image.sizes} alt={article.image.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 hover:scale-103" />
            </div>
            <CardHeader className="space-y-3 p-6 pb-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Badge variant="secondary" className="font-semibold text-foreground">
                  {article.category}
                </Badge>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <SafeHydrate><Calendar className="h-3.5 w-3.5" /></SafeHydrate>
                  <span>{article.date}</span>
                </div>
              </div>
              <h3 className="font-serif text-xl font-semibold leading-snug text-foreground">
                {article.title}
              </h3>
            </CardHeader>
            <CardContent className="flex-1 space-y-4 p-6 pt-2">
              <p className="text-sm leading-relaxed text-muted-foreground">
                {article.excerpt}
              </p>

              <div className="rounded-lg border border-border border-l-4 border-l-foreground/80 bg-background/80 p-4">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  <span>Clinical summary</span>
                </div>
                <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                  {article.takeaway}
                </p>
              </div>

              <div className="flex items-center gap-2 border-t border-border pt-4">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary text-secondary-foreground">
                  <UserCheck className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium text-foreground">
                    Reviewed by {article.reviewer}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    Evidence verified
                  </p>
                </div>
              </div>
            </CardContent>
            <CardFooter className="p-6 pt-0">
              <Button asChild variant="default" className="w-full justify-between">
                <Link to={article.link}>
                  <span>Read clinical guide</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </CardFooter>
          </Card>
        </article>)}
    </div>;
}
export default function Articles() {
  const wpQuery = useMemo(() => {
    return new WP_Query({
      post_type: "post",
      per_page: 3,
      order: "desc",
      orderby: "date"
    });
  }, []);
  return <section id="featured-health-articles" data-nav="light" className="bg-background py-16 md:py-24 text-foreground border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
              <Stethoscope className="h-3.5 w-3.5" />
              <span>Veterinary Medical Board</span>
            </div>
            <h2 className="font-serif text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
              Featured clinical guidance
            </h2>
            <p className="mt-3 text-base text-muted-foreground md:text-lg">
              Read veterinary-reviewed guides on symptoms, preventative care, and managing common chronic conditions.
            </p>
          </div>
          <Button asChild variant="outline" className="hidden sm:inline-flex">
            <Link to="/PetHealthPage">View all health guides</Link>
          </Button>
        </div>

        <PostsProvider wp_query={wpQuery}>
          <ArticlesConsumer />
        </PostsProvider>

        <div className="mt-8 text-center sm:hidden">
          <Button asChild variant="outline" className="w-full">
            <Link to="/PetHealthPage">View all health guides</Link>
          </Button>
        </div>
      </div>
    </section>;
}